import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import safeStorage from "@/lib/safeStorage";
import StaffLayout from "@/components/StaffLayout";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, QrCode, Download, Copy, ExternalLink, X } from "lucide-react";

export default function AdminTables() {
  const { t } = useLang();
  const [tables, setTables] = useState([]);
  const [number, setNumber] = useState("");
  const [label, setLabel] = useState("");
  const [qrTable, setQrTable] = useState(null);
  const [qrBlob, setQrBlob] = useState(null);

  const load = async () => {
    const { data } = await api.get("/tables");
    setTables(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!number) return;
    try {
      await api.post("/tables", { number: Number(number), label: label || null });
      toast.success("Table added");
      setNumber(""); setLabel("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this table?")) return;
    await api.delete(`/tables/${id}`);
    toast.success("Removed");
    load();
  };

  const showQr = async (t) => {
    setQrTable(t);
    setQrBlob(null);
    const token = safeStorage.getItem("token");
    const res = await fetch(`${API}/tables/${t.id}/qr`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    setQrBlob(URL.createObjectURL(blob));
  };

  const tableUrl = (code) => `${window.location.origin}/t/${code}`;

  return (
    <StaffLayout>
      <div className="overline text-zinc-500">{t("admin")}</div>
      <h1 className="font-condensed text-5xl font-bold tracking-tight mt-1">{t("tables_qr_title")}</h1>

      <form onSubmit={create} className="mt-8 flex flex-wrap gap-3 items-end" data-testid="new-table-form">
        <div>
          <label className="overline text-zinc-500">{t("number_lbl")}</label>
          <input type="number" value={number} onChange={(e) => setNumber(e.target.value)} className="mt-2 px-3 py-2 w-24 font-plex text-sm" required data-testid="new-table-number" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="overline text-zinc-500">{t("label_optional")}</label>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} className="mt-2 px-3 py-2 w-full font-plex text-sm" placeholder="Window seat, Patio 3…" data-testid="new-table-label" />
        </div>
        <button type="submit" data-testid="add-table-btn" className="bg-white text-black px-4 py-2 font-plex text-sm hover:bg-[#007aff] hover:text-white transition-colors flex items-center gap-2">
          <Plus size={14} /> {t("add_table")}
        </button>
      </form>

      <div className="mt-10 border border-[#262626]">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-[#262626] overline text-zinc-500">
          <div className="col-span-2">{t("number_lbl") || "Number"}</div>
          <div className="col-span-3">{t("label_lbl") || "Label"}</div>
          <div className="col-span-5">{t("public_url") || "Public URL"}</div>
          <div className="col-span-2 text-right">{t("actions")}</div>
        </div>
        {tables.map((tbl) => (
          <div key={tbl.id} className="grid grid-cols-12 px-4 py-3 border-b border-[#262626] last:border-0 items-center font-plex text-sm" data-testid={`table-row-${tbl.id}`}>
            <div className="col-span-2 font-condensed text-2xl font-bold">{String(tbl.number).padStart(2, "0")}</div>
            <div className="col-span-3 text-zinc-300 truncate">{tbl.label}</div>
            <div className="col-span-5 text-zinc-400 truncate flex items-center gap-2">
              <code className="text-xs">{tableUrl(tbl.code)}</code>
              <button onClick={() => { navigator.clipboard.writeText(tableUrl(tbl.code)); toast.success(t("url_copied")); }} className="text-zinc-500 hover:text-white" data-testid={`copy-url-${tbl.id}`}><Copy size={12} /></button>
              <a href={tableUrl(tbl.code)} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-white"><ExternalLink size={12} /></a>
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <button onClick={() => showQr(tbl)} data-testid={`qr-btn-${tbl.id}`} className="border border-[#262626] px-3 py-1.5 text-xs hover:bg-white hover:text-black transition-colors flex items-center gap-1.5"><QrCode size={12} /> QR</button>
              <button onClick={() => remove(tbl.id)} data-testid={`delete-table-${tbl.id}`} className="border border-[#262626] px-3 py-1.5 text-xs hover:bg-[#ff3b30] hover:text-white hover:border-[#ff3b30] transition-colors"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>

      {qrTable && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setQrTable(null)}>
          <div className="bg-[#0a0a0a] border border-[#262626] max-w-sm w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-baseline justify-between">
              <div>
                <div className="overline text-zinc-500">{t("qr_code")}</div>
                <div className="font-condensed text-3xl font-bold">{qrTable.label}</div>
              </div>
              <button onClick={() => setQrTable(null)} data-testid="close-qr-modal" className="text-zinc-500"><X size={18} /></button>
            </div>
            <div className="mt-6 bg-white p-6 flex items-center justify-center">
              {qrBlob ? <img src={qrBlob} alt="QR" className="w-full max-w-[240px]" /> : <div className="w-[240px] h-[240px] flex items-center justify-center text-black font-plex text-sm">{t("generating")}</div>}
            </div>
            <div className="mt-4 text-xs text-zinc-500 font-plex break-all">{tableUrl(qrTable.code)}</div>
            {qrBlob && (
              <a href={qrBlob} download={`table-${qrTable.number}-qr.png`} data-testid="download-qr-btn" className="mt-4 w-full bg-white text-black py-3 flex items-center justify-center gap-2 font-plex text-sm hover:bg-[#007aff] hover:text-white transition-colors">
                <Download size={14} /> {t("download_png")}
              </a>
            )}
          </div>
        </div>
      )}
    </StaffLayout>
  );
}
